import React, { useState, useEffect, useCallback } from 'react';
import bonsLivraisonApi from '../services/bons-livraison';
import './CarnetCommande.css';

function formatMontant(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export default function BonsLivraison({ user, setHeaderAction }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filtreSociete, setFiltreSociete] = useState('');
  const [syncing, setSyncing]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState(null);
  const [syncErr, setSyncErr]   = useState(null);
  const [modal, setModal]       = useState(null);

  // Pas de bouton d'action dans le header

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bonsLivraisonApi.getAll();
      setRows(res.data);
    } catch {
      // silencieux
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Société affichée : nom de concession mappé (societe_nom) ou fallback code brut.
  const societeLabel = (r) => r.societe_nom || r.societe;
  const societes = [...new Set(rows.map(societeLabel).filter(Boolean))].sort();

  const displayed = rows.filter(r => {
    const label = societeLabel(r);
    if (filtreSociete && label !== filtreSociete) return false;
    if (!filtreSociete && user?.concession_nom && label !== user.concession_nom) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.num_or   || '').toLowerCase().includes(q) ||
      (r.id_cde   || '').toLowerCase().includes(q) ||
      (r.nom_rcp  || '').toLowerCase().includes(q) ||
      (r.libelle  || '').toLowerCase().includes(q)
    );
  });

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    setSyncErr(null);
    try {
      const res = await bonsLivraisonApi.sync();
      setSyncMsg(res.data.message || 'Synchronisation réussie');
      await load();
    } catch (err) {
      setSyncErr(err.response?.data?.error || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="cc-wrap">
      {/* Messages sync */}
      {syncMsg && <div className="cc-sync-ok">✅ {syncMsg}</div>}
      {syncErr && <div className="cc-sync-err">❌ {syncErr}</div>}

      {/* Recherche + filtres */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={`page-select${filtreSociete ? ' active-filter' : ''}`}
          value={filtreSociete}
          onChange={e => setFiltreSociete(e.target.value)}
        >
          <option value="">Toutes les sociétés</option>
          {societes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="cc-count">{displayed.length} BL{displayed.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="cc-loading">Chargement…</div>
      ) : displayed.length === 0 ? (
        <div className="cc-empty">Aucun bon de livraison.{rows.length === 0 ? ' Cliquez sur "Synchroniser SFTP" pour importer les données.' : ''}</div>
      ) : (
        <div className="table-container">
          <table className="cc-table">
            <thead>
              <tr>
                <th>N° OR</th>
                <th>N° Commande</th>
                <th>Société</th>
                <th>Réceptionnaire</th>
                <th>Libellé</th>
                <th>Nb lignes</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(bl => (
                <tr key={bl.id} className="cc-row" onClick={() => setModal(bl)}>
                  <td className="cc-or">{bl.num_or || '—'}</td>
                  <td>{bl.id_cde || '—'}</td>
                  <td title={bl.societe || ''}>{bl.societe_nom || bl.societe || '—'}</td>
                  <td>{bl.nom_rcp || '—'}</td>
                  <td>{bl.libelle || '—'}</td>
                  <td className="cc-nb">{bl.nb_lignes || 0}</td>
                  <td className="cc-montant">{formatMontant(bl.total)}</td>
                  <td className="cc-detail-btn-cell">
                    <button className="cc-detail-btn" onClick={e => { e.stopPropagation(); setModal(bl); }}>
                      Détail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale détail lignes BL */}
      {modal && (
        <div className="cc-modal-overlay">
          <div className="cc-modal" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header">
              <div>
                <h3 className="cc-modal-title">
                  {modal.num_or ? `OR ${modal.num_or}` : `Cde ${modal.id_cde || '—'}`}
                </h3>
                <div className="cc-modal-subtitle">
                  {modal.nom_rcp && <span>{modal.nom_rcp}</span>}
                  {modal.libelle && <span> · {modal.libelle}</span>}
                  {modal.societe && <span> · Société {modal.societe_nom || modal.societe}</span>}
                </div>
              </div>
              <button className="cc-modal-close" onClick={() => setModal(null)}>✕</button>
            </div>

            <div className="cc-modal-body">
              {(!modal.lignes_json || modal.lignes_json.length === 0) ? (
                <p className="cc-modal-empty">Aucune ligne.</p>
              ) : (
                <div className="cc-modal-table-wrap">
                  <table className="cc-modal-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Réf. pièce</th>
                        <th>Description</th>
                        <th>Qté cdée</th>
                        <th>Qté livrée</th>
                        <th>Total ligne</th>
                        <th>Réceptionnaire</th>
                        <th>Commentaire</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modal.lignes_json.map((l, i) => (
                        <tr key={i}>
                          <td className="cc-modal-num">{i + 1}</td>
                          <td className="cc-modal-ref">{l.id_pie || '—'}</td>
                          <td className="cc-modal-desc">{l.des || '—'}</td>
                          <td className="cc-modal-center">{l.qte_cde ?? 0}</td>
                          <td className="cc-modal-center">{l.qte_liv ?? 0}</td>
                          <td className="cc-modal-right">{formatMontant(l.tot_ligne)}</td>
                          <td>{l.nom_rcp || '—'}</td>
                          <td className="cc-modal-comment">{l.comment || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className="cc-modal-total-label">Total</td>
                        <td className="cc-modal-right cc-modal-total">{formatMontant(modal.total)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
